import { createOffline } from '@redux-offline/redux-offline'
import defaultOfflineConfig from '@redux-offline/redux-offline/lib/defaults'
import { effect } from '@tellonym/core/api'
import { errorHandlerMiddleware } from '@tellonym/core/store/middleware/errorHandlerMiddleware'
import { requestMiddleware } from '@tellonym/core/store/middleware/requestMiddleware'
import { queue } from '@tellonym/core/store/queue'
import { retry } from '@tellonym/core/store/retry'
import { mergeDeepRight } from 'ramda'
import { applyMiddleware, compose, createStore } from 'redux'
import { createLogger } from 'redux-logger'
import { persistReducer, persistStore } from 'redux-persist'
import createSagaMiddleware from 'redux-saga'
import thunk from 'redux-thunk'
import { config } from '../../config'
import { Alert, _ } from '../common'
import { rootSaga } from '../sagas/rootSaga'
import { errorHandler } from './errorHandler'
import { stateInjection } from './middleware/stateInjection'
import { persistOptions, rootReducer } from './rootReducer'

const {
  middleware: offlineMiddleware,
  enhanceReducer,
  enhanceStore,
} = createOffline({
  ...defaultOfflineConfig,
  ...mergeDeepRight(config.store.offline, {
    persist: false,
    effect,
    queue,
    retry,
  }),
})

const persistedReducer = persistReducer(
  mergeDeepRight(persistOptions, config.store.offline.persistOptions),
  enhanceReducer(rootReducer)
)

const composeEnhancers =
  process.env.NODE_ENV === 'development' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose

export const configureStore = () => {
  const sagaMiddleware = createSagaMiddleware({
    onError: (e) => _.capture(e),
  })

  /**
   * The thunk middleware as to be first position so that other middlewares don't need to check if the action is a function.
   * The stateInjection comes right after as other middlewares rely on it.
   * The requestMiddleware has to be in front of the offlineMiddleware as it might decide to skip it.
   */
  const middleware = [
    thunk,
    stateInjection,
    requestMiddleware,
    offlineMiddleware,
    sagaMiddleware,
    process.env.NODE_ENV === 'development' && createLogger(config.reduxLogger),
    errorHandlerMiddleware({
      badConnectionHandler: ({ type }) => {
        if (config.api.errorBlacklist.indexOf(type) === -1) {
          Alert.error('no internet')
        }
      },
      errorHandler,
    }),
  ].filter(Boolean)

  const store = createStore(
    persistedReducer,
    composeEnhancers(enhanceStore, applyMiddleware(...middleware))
  )

  sagaMiddleware.run(rootSaga)

  const persistor = persistStore(store)

  if (process.env.NODE_ENV === 'development') {
    window.__STORE__ = store
  }

  return [store, persistor]
}
